import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ShoppingCart } from 'lucide-react';
import { useCartStore } from '../store/cart';
import { useAuthStore } from '../store/authStore';
import { DesktopNav } from './navigation/DesktopNav';
import { MobileMenu } from './navigation/MobileMenu';

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { items } = useCartStore();
  const { user, signOut } = useAuthStore();

  const isActive = (path: string) => location.pathname === path;

  // Simple toggle for mobile menu
  const toggleMobileMenu = () => {
    setIsOpen(!isOpen);
    document.body.style.overflow = !isOpen ? 'hidden' : '';
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
  }, [location.pathname]);

  // Clean up body scroll lock when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Escape key closes mobile menu
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        aria-label="Main navigation"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/90 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <Link to="/" className="flex items-center h-16 py-2 transition-transform duration-300 hover:scale-105 animate-fadeIn">
                <img
                  src="/JME_fit_black_purple.png"
                  alt="JmeFit Training"
                  className="h-12 w-auto object-contain"
                  style={{ aspectRatio: 'auto' }}
                  onError={(e) => {
                    const imgElement = e.currentTarget;
                    imgElement.onerror = null;
                    imgElement.src = 'https://jmefit.netlify.app/JME_fit_black_purple.png';
                  }}
                />
              </Link>
            </div>

            <DesktopNav
              isActive={isActive}
              user={user}
              signOut={signOut}
              itemCount={items.length}
            />

            {/* Mobile Menu Button */}
            <div className="flex items-center md:hidden">
              <div className="flex items-center gap-2">
                <Link
                  to="/checkout"
                  className="relative p-2 rounded-md text-jme-purple hover:text-white hover:bg-jme-purple transition-colors duration-200"
                  aria-label="Go to checkout"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {items.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {items.length}
                    </span>
                  )}
                </Link>
                <button
                  onClick={toggleMobileMenu}
                  className="inline-flex items-center justify-center p-2 rounded-md text-jme-purple hover:text-white hover:bg-jme-purple transition-all duration-300 transform hover:scale-105 btn-hover-effect"
                  aria-expanded={isOpen}
                  aria-label="Toggle navigation menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full Screen Overlay */}
      <MobileMenu
        isOpen={isOpen}
        isActive={isActive}
        user={user}
        signOut={signOut}
        onToggle={toggleMobileMenu}
      />
    </>
  );
}

export default Navigation;
